import { ButtonIcon, Card, Container, Heading } from 'ui/components/primitives';
import { mixins, styled, theme, utils } from 'ui/theme';

const General = {
  Component: styled.div`
    ${mixins.baseFont};
    margin: ${theme.space[0]} 0 ${theme.space[6]} 0;

    &[data-margin='sm'] {
      margin: ${theme.space[0]} 0 ${theme.space[2]} 0;
    }
  `,

  Payment: styled.div`
    ${mixins.baseFont};
    margin: ${theme.space[0]} ${theme.space[0]};
  `,

  Row: styled.div`
    ${utils.flexbox.direction.row};
    justify-content: space-between;
    width: 100%;
  `,

  Text: styled.span`
    align-items: center;
    color: ${theme.colors.build.darkLight};
    display: flex;
    font-size: ${theme.fontSizes.md};
  `,

  Title: styled.span`
    ${mixins.baseFont};
    ${utils.flexbox.direction.row};
    align-items: center;
    color: ${theme.colors.build.darkLight};
    font-size: ${theme.fontSizes.lg};
    gap: ${theme.space[1]};
    margin-bottom: ${theme.space[3]};
  `,
};

export const AvailableCredit = {
  Card: styled(Card)`
    padding: 0 ${theme.space[2]};
    margin: 0 0 ${theme.space[4]} 0;
  `,
};

export const Cta = {
  ButtonWrapper: styled.div`
    padding-bottom: 50px;
  `,
  ButtonContainer: styled.div`
    position: fixed;
    bottom: 0;
    width: 100%;
    max-width: 536px;
    @media (max-width: 536px) {
      padding-right: 32px;
    }
  `,
  Button: styled(ButtonIcon)`
    color: ${theme.colors.build.lightLight};
    display: flex;
    grid-gap: ${theme.space[3]};
    margin: ${theme.space[6]} 0;
    ${utils.media.max('s')`
      position: fixed;
      bottom: 0;
      left: 0;
      border-radius: 0;
      margin: 0;
    `}
  `,
};

export const Address = {
  ...General,

  Text: styled(General.Text)`
    text-transform: capitalize;
  `,
};

export const Items = {
  ...General,

  Card: styled(Card)`
    margin-bottom: ${theme.space[2]};
    [data-testid='tooltip'] {
      position: absolute;
      right: 30px;
    }
  `,

  CartInfo: styled.span`
    display: flex;
    flex-flow: column;
    gap: ${theme.space[1]};
    grid-column: auto / span 1;

    &[data-start] {
      grid-column: 2 / span 1;
    }

    small {
      font-size: ${theme.fontSizes.xs};
    }
  `,

  Component: styled.div`
    margin: ${theme.space[0]} 0 ${theme.space[6]} 0;
    width: 100%;
    &[data-margin='sm'] {
      margin: ${theme.space[0]} 0 ${theme.space[2]} 0;
    }
  `,

  Details: styled.div`
    ${utils.grid.size[4]};
    gap: ${theme.space[4]} ${theme.space[2]};
    grid-template-columns: auto auto auto 1fr;
    width: 100%;

    ${utils.media.min('s')`
      gap: ${theme.space[4]} ${theme.space[5]};
    `}
  `,

  Image: styled.span`
    width: ${theme.sizes[8]};
    height: ${theme.sizes[8]};
    border: 1px solid ${theme.colors.build.lightDark1};
    border-radius: ${theme.radii.small};
    overflow: hidden;
  `,

  ProductInfo: styled.span`
    display: flex;
    flex-flow: column;
    gap: ${theme.space[1]};
    grid-column: auto / span 3;

    small {
      font-size: ${theme.fontSizes.xs};
    }

    span {
      ${utils.lineClamp(2)}
      font-size: ${theme.fontSizes.md};
      margin: 0;
    }
  `,

  Row: styled(General.Row)`
    gap: ${theme.space[2]};
  `,
};

export const LoaderSummary = {
  Component: styled(Container)`
    align-items: center;
    display: flex;
    flex-direction: column;
  `,

  Text: styled(Heading)`
    color: ${theme.colors.build.brand1};
    margin-top: ${theme.space[6]};
  `,
};

export const Payment = {
  ...General,
  Row: styled(General.Row)`
    align-items: center;

    *:nth-of-type(2n) {
      text-align: right;
    }
  `,
};

export const Reward = {
  ...General,

  Card: styled(Card)`
    margin: 0 0 ${theme.space[2]} 0;
  `,

  Info: styled.div`
    align-items: center;
    display: flex;
    gap: ${theme.space[4]};
    grid-template-columns: auto auto auto 1fr;
    justify-content: space-between;
    width: 100%;
  `,

  Text: styled(General.Text)`
    flex-grow: 1;
  `,
  NextOrderAllowedMsg: styled(General.Text)`
    margin: -7px 0 12px;
  `,
};

export const ValuesBankSlip = {
  ...General,

  Text: styled(General.Text)`
    flex-grow: 1;
  `,
};

export const Values = {
  ...General,

  Divider: styled.span`
    background: ${theme.colors.build.mediumLight1};
    display: block;
    height: 1px;
    margin: ${theme.space[1]} 0;
    width: 100%;
  `,

  Text: styled(General.Text)`
    &[data-color='brand-1'] {
      color: ${theme.colors.build.brand1};
    }

    &[data-fontsize='lg'] {
      font-size: ${theme.fontSizes.lg};
    }

    &[data-fontsize='2xl'] {
      font-size: ${theme.fontSizes.xxl};
    }

    &[data-weight='heavy'] {
      font-weight: ${theme.fontWeights.heavy};
    }
  `,

  Title: styled(General.Title)`
    margin-top: ${theme.space[5]};
  `,

  Row: styled(General.Row)`
    align-items: center;
    margin: ${theme.space[3]} 0;
    padding: ${theme.space[0]} 0;

    &[data-spacing='none'] {
      margin: ${theme.space[0]};
      padding: ${theme.space[0]};
    }

    &[data-margin-top='none'] {
      margin-top: ${theme.space[0]};
    }

    &[data-margin-bottom='none'] {
      margin-bottom: ${theme.space[0]};
    }

    *:nth-of-type(2n) {
      text-align: right;
    }
  `,
};
